import { useEffect, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { Form, FormSelect, FormTextField, getIn } from '../../Formik/forms';
import { stripHTML, titlecase } from '../../../lib/utils'

import DeleteIcon from '@mui/icons-material/Delete';
import AssignmentIcon from '@mui/icons-material/Assignment';

import { saveWithPayload } from '../../../lib/DataModel'
import { useNotifications } from 'lib/NotificationsProvider';
import { PageSection } from 'components/UI/Structure/PageSection';
import SectionTitle from 'components/UI/SectionTitle';
import { useOrbit } from 'providers/OrbitProvider';
import { useCurrentUser } from 'lib/CurrentUserProvider';
import { UnauthorizedError } from 'lib/errors/UnauthorizedError';
import { useErrorBoundary } from 'react-error-boundary';
import OptionalTooltip from 'components/UI/helpers/OptionalTooltip';
import MenuItem from '@mui/material/MenuItem';


const DEGREE_PROGRAM_STATUSES = ['active', 'hidden', 'inactive']

function fetchProgram(remote, id) {
  return remote.query(q => q.findRecord({type: "degreeProgram", id: id}))
}

function newProgram(clientId, programGroupId) {
  return Promise.resolve({
    type: "degreeProgram",
    attributes: {
    },
    relationships: {
      client: {
        data: { type: 'client', id: clientId },
      },
      programGroup: {
        data: { type: 'programGroup', id: programGroupId },
      },
    },
  })
}

export default function DegreeProgramEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { remote } = useOrbit()
  const { addNotification } = useNotifications()
  const { clientId, degreeProgramId } = useParams()
  const [program, setProgram] = useState();
  const [_client, setClient] = useState();
  const [loading, setLoading] = useState(true);
  const { getClientRole } = useCurrentUser()
  const { showBoundary } = useErrorBoundary()

  const permitEdit = !!getIn(getClientRole(clientId), 'attributes.permitEdit')

  useEffect(() => {
    const promises = [
      degreeProgramId ? fetchProgram(remote, degreeProgramId) : newProgram(clientId, new URLSearchParams(location.search).get('programGroupId')),
      clientId ? remote.query(q => q.findRecord({type: 'client', id: clientId})) : undefined,
    ];
    Promise.all(promises).then(([program, client]) => {
      // If the program doesn't match the client id from path, navigate to the top level degree-programs page
      if(getIn(program, 'relationships.client.data.id') !== clientId) {
        navigate(`/clients/${clientId}/degree-programs`)
      }

      setProgram(program);
      setClient(client);
      setLoading(false);
    }).catch(error => {
      if(error instanceof UnauthorizedError) {
        showBoundary(error)
      }

      console.log(error)
      addNotification({variant: 'alert', message: "Error loading degree program"});
      setLoading(false);
    })
  }, [addNotification, clientId, degreeProgramId, location.search, navigate, remote, showBoundary]);

  if(loading) {
    return (
      <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
        <CircularProgress/>
      </div>
    )
  } else {
    const deleteProgram = ({deleteRecord}) => () => {
      if (!confirm(`Are you sure you want to delete ${stripHTML(program.attributes.name)}?`)) {
        return;
      }
      deleteRecord(program).then(success => {
        if(success) {
          addNotification({variant: 'notice', message: `Deleted degree program ${stripHTML(program.attributes.name)}`})
          navigate(`/clients/${clientId}/degree-programs`)
        }
      })
    };

    // TODO: set clientId via route
    const save = (programAttributes) => {
      return saveWithPayload(remote, program, {
        data: {
          attributes: {
            ...remote.requestProcessor.serializer.serializeRecord({type: 'degreeProgram', id: degreeProgramId, attributes: programAttributes}).attributes,
            'client-id': getIn(programAttributes, 'relationships.client.data.id') || null,
            'program-group-id': getIn(programAttributes, 'relationships.programGroup.data.id') || null,
          }
        },
      });
    };

    return (
      <PageSection>
        <SectionTitle title='Degree Programs' />
        <Form
          resource={program}
          setResource={setProgram}
          customSave={save}
          initialValues={{...program.attributes, relationships: program.relationships}}
          redirectAfterSave={clientId ? true : '/manage/degree-programs'}
          withoutSubmitButton={!permitEdit}
          backwardsCompatibleMargins={true}
        >
          {({deleteRecord}) => (
            <>
              <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                  <Typography variant="h6">
                    {program.id ? 'Edit' : 'New'} degree program
                  </Typography>
                </Grid>
                {program.id && (
                  <Grid item>
                    <OptionalTooltip title={permitEdit && "Delete this degree program"}>
                      <IconButton aria-label="Delete this degree program" onClick={deleteProgram({deleteRecord})} size="large" disabled={!permitEdit}>
                        <DeleteIcon/>
                      </IconButton>
                    </OptionalTooltip>
                    <Tooltip title="Edit exclusion rules">
                      <IconButton aria-label="Edit exclusion rules" onClick={() => navigate(`./exclusion-rules`)} size="large">
                        <AssignmentIcon/>
                      </IconButton>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
              <FormTextField
                label="Name"
                name="name"
                disabled={!permitEdit}
              />
              <FormTextField
                label="Program Code"
                name="programCode"
                disabled={!permitEdit}
              />
              <FormSelect
                label="Status"
                name="status"
                sx={{width: 200}}
                disabled={!permitEdit}
              >
                {DEGREE_PROGRAM_STATUSES.map(status => (
                  <MenuItem key={status} value={status}>{titlecase(status)}</MenuItem>
                ))}
              </FormSelect>
            </>
          )}
        </Form>
      </PageSection>
    );
  }
}
